import kubLogo from "../assets/logos/kyb-tv-logo.svg";

export type HousingType = "residential" | "commerce";

export type HousingFilterRule = {
  ids?: number[];
  housingNames?: string[];
  housingNameIncludes?: string[];
  builders?: string[];
  builderIncludes?: string[];
};

type ListTabContent = {
  title: string;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
};

export type ShowcasePalette = {
  pageBackgroundStart: string;
  pageBackground: string;
  pageBackgroundEnd: string;
  surface: string;
  surfaceAlt: string;
  surfaceElevated: string;
  border: string;
  accent: string;
  accentStrong: string;
  accentContrast: string;
  textPrimary: string;
  textSecondary: string;
  gridLine: string;
  glowPrimary: string;
  glowSecondary: string;
  badgeBackground: string;
  badgeText: string;
  tabActiveBackground: string;
  tabActiveText: string;
  inputBackground: string;
  inputHighlight: string;
  secondaryButtonBackground: string;
  secondaryButtonText: string;
  secondaryButtonBorder: string;
  skeletonBase: string;
  skeletonHighlight: string;
};

export type ClientConfig = {
  id: string;
  displayName: string;
  searchPlaceholder: string;
  housingCard: {
    showBuilder: boolean;
    defaultBuilderLabel: string;
    unknownAddressLabel: string;
    unknownDeadlineLabel: string;
    priceLabel: string;
    priceFallbackLabel: string;
    priceNoteLabel: string;
    priceFallbackNoteLabel: string;
    openLabel: string;
  };
  housingsPage: {
    tabs: Record<HousingType, ListTabContent>;
    filtersButtonLabel: string;
    totalObjectsLabel: string;
    shownObjectsLabel: string;
    resetFiltersLabel: string;
    allObjectsLabel: string;
    foundObjectsLabel: (shownCount: number, totalCount: number) => string;
    deadlineRangeLabel: (startYear: number, endYear: number) => string;
    excludeUnknownDeadlinesLabel: string;
  };
  housingDetails: {
    backLabel: string;
    unavailableTitle: string;
    heroKicker: string;
    limitedHeroKicker: string;
    descriptionTitle: string;
    descriptionEmptyLabel: string;
    expandDescriptionLabel: string;
    collapseDescriptionLabel: string;
    limitedDescriptionLabel: string;
    factsTitle: string;
    factsEmptyLabel: string;
    factLabels: {
      address: string;
      deadline: string;
      builder: string;
    };
    navigationTitle: string;
    navigationHeading: string;
    pricingAnchorLabel: string;
    materialsAnchorLabel: string;
    materialsSectionEyebrow: string;
    materialsSectionTitle: string;
    accessDeniedTitle: string;
    accessDeniedDescription: string;
    showMaterialsSection: boolean;
    showNavigationSection: boolean;
    showFactsSection: boolean;
    showBuilder: boolean;
  };
  pricing: {
    showPriceList: boolean;
    showCalculator: boolean;
    eyebrow: string;
    title: string;
    mobileOpenLabel: string;
    mobileCloseLabel: string;
  };
  housingAccess: Partial<Record<HousingType, HousingFilterRule>>;
  showcase: {
    enabledHousingTypes: HousingType[];
    heroLabel: string;
    heroTitle: string;
    heroDescription: string;
    contactName: string;
    phone: string;
    whatsapp: string;
    telegram: string;
    instagram: string;
    whatsappNumber: string;
    whatsappButtonLabel: string;
    logoMode: "text" | "image" | "image-text";
    logoLayout: "text-right" | "text-bottom";
    logoWidth: number;
    logoImageHeight: number;
    logoTextSize: number;
    logoTextColor: string;
    logoText: string;
    logoSrc?: string;
    logoAlt?: string;
    palette: ShowcasePalette;
  };
};

export type ClientConfigDraft = {
  id?: string;
  displayName?: string;
  searchPlaceholder?: string;
  housingCard?: Partial<ClientConfig["housingCard"]>;
  housingsPage?: {
    tabs?: Partial<Record<HousingType, Partial<ListTabContent>>>;
    filtersButtonLabel?: string;
    totalObjectsLabel?: string;
    shownObjectsLabel?: string;
    resetFiltersLabel?: string;
    allObjectsLabel?: string;
    excludeUnknownDeadlinesLabel?: string;
  };
  housingDetails?: Partial<Omit<ClientConfig["housingDetails"], "factLabels">> & {
    factLabels?: Partial<ClientConfig["housingDetails"]["factLabels"]>;
  };
  pricing?: Partial<ClientConfig["pricing"]>;
  housingAccess?: Partial<Record<HousingType, HousingFilterRule>>;
  showcase?: Partial<Omit<ClientConfig["showcase"], "palette">> & {
    palette?: Partial<ShowcasePalette>;
  };
};

const normalizeText = (value: string) => value.trim().toLowerCase();

export const normalizeBrandSlug = (value?: string) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_а-яё]/gi, "");

const parseCsvEnv = (value?: string): string[] =>
  (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const parseIdEnv = (value?: string): number[] =>
  parseCsvEnv(value)
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));

const createFilterRuleFromEnv = (scope: string): HousingFilterRule | undefined => {
  const ids = parseIdEnv(process.env[`REACT_APP_${scope}_IDS`]);
  const housingNames = parseCsvEnv(process.env[`REACT_APP_${scope}_HOUSING_NAMES`]);
  const housingNameIncludes = parseCsvEnv(
    process.env[`REACT_APP_${scope}_HOUSING_NAME_INCLUDES`],
  );
  const builders = parseCsvEnv(process.env[`REACT_APP_${scope}_BUILDERS`]);
  const builderIncludes = parseCsvEnv(
    process.env[`REACT_APP_${scope}_BUILDER_INCLUDES`],
  );

  if (
    ids.length === 0 &&
    housingNames.length === 0 &&
    housingNameIncludes.length === 0 &&
    builders.length === 0 &&
    builderIncludes.length === 0
  ) {
    return undefined;
  }

  return {
    ids: ids.length ? ids : undefined,
    housingNames: housingNames.length ? housingNames : undefined,
    housingNameIncludes: housingNameIncludes.length ? housingNameIncludes : undefined,
    builders: builders.length ? builders : undefined,
    builderIncludes: builderIncludes.length ? builderIncludes : undefined,
  };
};

export const defaultClientConfigDraft: ClientConfigDraft = {
  id: "husamhelper",
  displayName: "HusamHelper",
  searchPlaceholder: "поиск по названию",
  housingCard: {
    showBuilder: true,
    defaultBuilderLabel: "Объект недвижимости",
    unknownAddressLabel: "Адрес уточняется",
    unknownDeadlineLabel: "Срок не указан",
    priceLabel: "Цена",
    priceFallbackLabel: "По запросу",
    priceNoteLabel: "₽ за м²",
    priceFallbackNoteLabel: "Уточните у менеджера",
    openLabel: "Открыть",
  },
  housingsPage: {
    tabs: {
      residential: {
        title: "Жилые комплексы",
        description:
          "Подборка ЖК в спокойном списочном формате для быстрого просмотра и перехода в карточку.",
        emptyTitle: "По этим параметрам ничего не найдено",
        emptyDescription:
          "Измените строку поиска или сбросьте фильтр по сроку сдачи, чтобы снова увидеть весь список.",
      },
      commerce: {
        title: "Коммерческие комплексы",
        description:
          "Список объектов с быстрым поиском и аккуратной фильтрацией по сроку сдачи.",
        emptyTitle: "По этим параметрам ничего не найдено",
        emptyDescription:
          "Измените строку поиска или сбросьте фильтр по сроку сдачи, чтобы снова увидеть весь список.",
      },
    },
    filtersButtonLabel: "Фильтры",
    totalObjectsLabel: "Всего объектов",
    shownObjectsLabel: "Показано",
    resetFiltersLabel: "Сбросить фильтр",
    allObjectsLabel: "Все объекты",
    excludeUnknownDeadlinesLabel: "Без неизвестных сроков",
  },
  housingDetails: {
    backLabel: "Ко всем объектам",
    unavailableTitle: "Объект не найден",
    heroKicker: "Карточка объекта",
    limitedHeroKicker: "Объект без доступа",
    descriptionTitle: "Об объекте",
    descriptionEmptyLabel: "Описание пока не добавлено.",
    expandDescriptionLabel: "Показать полностью",
    collapseDescriptionLabel: "Свернуть описание",
    limitedDescriptionLabel:
      "Для просмотра подробного описания, цен и материалов нужен вход в систему.",
    factsTitle: "Кратко об объекте",
    factsEmptyLabel: "Ключевые данные по объекту пока не заполнены.",
    factLabels: {
      address: "Адрес",
      deadline: "Срок сдачи",
      builder: "Застройщик",
    },
    navigationTitle: "Навигация",
    navigationHeading: "Быстрый переход",
    pricingAnchorLabel: "К ценам и калькулятору",
    materialsAnchorLabel: "К шахматкам и планировкам",
    materialsSectionEyebrow: "Материалы объекта",
    materialsSectionTitle: "Шахматки и планировки",
    accessDeniedTitle: "Нет доступа к разделу",
    accessDeniedDescription: "Для получения доступа необходимо войти",
    showMaterialsSection: true,
    showNavigationSection: true,
    showFactsSection: true,
    showBuilder: true,
  },
  pricing: {
    showPriceList: true,
    showCalculator: true,
    eyebrow: "Калькулятор",
    title: "Рассчитать рассрочку",
    mobileOpenLabel: "Открыть калькулятор",
    mobileCloseLabel: "Скрыть калькулятор",
  },
  housingAccess: {},
  showcase: {
    enabledHousingTypes: ["residential", "commerce"],
    heroLabel: "",
    heroTitle: "",
    heroDescription: "",
    contactName: "",
    phone: "",
    whatsapp: "",
    telegram: "",
    instagram: "",
    whatsappNumber: "",
    whatsappButtonLabel: "Отправить расчёт в WhatsApp",
    logoMode: "text",
    logoLayout: "text-right",
    logoWidth: 240,
    logoImageHeight: 52,
    logoTextSize: 34,
    logoTextColor: "#101828",
    logoText: "HusamHelper",
    logoAlt: "HusamHelper",
    palette: {
      pageBackgroundStart: "#eef2f9",
      pageBackground: "#f5f7fb",
      pageBackgroundEnd: "#edf1f8",
      surface: "#ffffff",
      surfaceAlt: "#f8fafc",
      surfaceElevated: "#ffffff",
      border: "#d9e0ea",
      accent: "#101828",
      accentStrong: "#182230",
      accentContrast: "#ffffff",
      textPrimary: "#101828",
      textSecondary: "#475467",
      gridLine: "rgba(16,24,40,0.04)",
      glowPrimary: "rgba(16,24,40,0.10)",
      glowSecondary: "rgba(24,34,48,0.08)",
      badgeBackground: "#101828",
      badgeText: "#ffffff",
      tabActiveBackground: "#ffffff",
      tabActiveText: "#101828",
      inputBackground: "#ffffff",
      inputHighlight: "#101828",
      secondaryButtonBackground: "#ffffff",
      secondaryButtonText: "#344054",
      secondaryButtonBorder: "#d0d5dd",
      skeletonBase: "#eef2f7",
      skeletonHighlight: "#f8fafc",
    },
  },
};

const kubResidentialFilter = createFilterRuleFromEnv("KUB_RESIDENTIAL");
const kubCommerceFilter = createFilterRuleFromEnv("KUB_COMMERCE");

export const kubClientConfigDraft: ClientConfigDraft = {
  ...defaultClientConfigDraft,
  id: "kub",
  displayName: "Куб",
  searchPlaceholder: "поиск по объектам KUB",
  housingCard: {
    ...defaultClientConfigDraft.housingCard,
    showBuilder: false,
    openLabel: "Открыть проект",
  },
  housingsPage: {
    ...defaultClientConfigDraft.housingsPage,
    tabs: {
      residential: {
        title: "Жилые комплексы",
        description:
          "Подборка ЖК в спокойном списочном формате для быстрого просмотра и перехода в карточку.",
        emptyTitle: "Для KUB пока не найдено подходящих ЖК",
        emptyDescription:
          "Измените параметры фильтрации или проверьте правила отбора объектов для бренда.",
      },
      commerce: {
        title: "Коммерческие объекты KUB",
        description:
          "Отдельный каталог коммерческих объектов для витрины KUB с тем же пользовательским сценарием.",
        emptyTitle: "Для KUB пока не найдено подходящих коммерческих объектов",
        emptyDescription:
          "Измените параметры фильтрации или проверьте правила отбора объектов для бренда.",
      },
    },
  },
  housingDetails: {
    ...defaultClientConfigDraft.housingDetails,
    backLabel: "К каталогу KUB",
    navigationHeading: "Разделы объекта",
    pricingAnchorLabel: "К цене и калькулятору",
    materialsAnchorLabel: "К материалам",
    showMaterialsSection: false,
    showBuilder: false,
  },
  pricing: {
    ...defaultClientConfigDraft.pricing,
    eyebrow: "Калькулятор KUB",
    title: "Рассчитать предложение",
    mobileOpenLabel: "Открыть расчёт",
    mobileCloseLabel: "Скрыть расчёт",
  },
  housingAccess: {
    residential: kubResidentialFilter,
    commerce: kubCommerceFilter,
  },
  showcase: {
    enabledHousingTypes: ["residential", "commerce"],
    heroLabel: "",
    heroTitle: "",
    heroDescription: "",
    contactName: "",
    phone: "",
    whatsapp: "",
    telegram: "",
    instagram: "",
    whatsappNumber: "",
    whatsappButtonLabel: "Отправить расчёт в WhatsApp",
    logoMode: "image",
    logoLayout: "text-right",
    logoWidth: 240,
    logoImageHeight: 52,
    logoTextSize: 34,
    logoTextColor: "#f5f7ff",
    logoText: "KUB",
    logoSrc: kubLogo,
    logoAlt: "KUB logo",
    palette: {
      pageBackgroundStart: "#060915",
      pageBackground: "#050816",
      pageBackgroundEnd: "#0a1021",
      surface: "#0c1222",
      surfaceAlt: "#10182c",
      surfaceElevated: "#121c33",
      border: "#22314f",
      accent: "#9badde",
      accentStrong: "#7f95d5",
      accentContrast: "#08101f",
      textPrimary: "#f5f7ff",
      textSecondary: "#aab6d3",
      gridLine: "rgba(255,255,255,0.018)",
      glowPrimary: "rgba(127,149,213,0.18)",
      glowSecondary: "rgba(155,173,222,0.14)",
      badgeBackground: "#9badde",
      badgeText: "#08101f",
      tabActiveBackground: "#33405f",
      tabActiveText: "#f5f7ff",
      inputBackground: "#121c33",
      inputHighlight: "#9badde",
      secondaryButtonBackground: "#0c1222",
      secondaryButtonText: "#f5f7ff",
      secondaryButtonBorder: "#22314f",
      skeletonBase: "#16213b",
      skeletonHighlight: "#22314f",
    },
  },
};

export const builtinBrandDrafts: Record<string, ClientConfigDraft> = {
  husamhelper: defaultClientConfigDraft,
  kub: kubClientConfigDraft,
};

const getPalette = (palette?: Partial<ShowcasePalette>): ShowcasePalette => ({
  ...defaultClientConfigDraft.showcase!.palette!,
  ...(palette || {}),
});

export const resolveClientConfig = (draft?: ClientConfigDraft): ClientConfig => {
  const incoming = draft || {};
  const incomingBrandId = normalizeBrandSlug(incoming.id || "");
  const base =
    builtinBrandDrafts[incomingBrandId] || defaultClientConfigDraft;
  const draftTabs = incoming.housingsPage?.tabs || {};
  const baseTabs = base.housingsPage!.tabs!;

  return {
    id: normalizeBrandSlug(incoming.id || base.id) || "husamhelper",
    displayName: incoming.displayName || base.displayName || "HusamHelper",
    searchPlaceholder: incoming.searchPlaceholder || base.searchPlaceholder || "",
    housingCard: {
      ...base.housingCard,
      ...(incoming.housingCard || {}),
    } as ClientConfig["housingCard"],
    housingsPage: {
      tabs: {
        residential: {
          ...baseTabs.residential,
          ...(draftTabs.residential || {}),
        } as ListTabContent,
        commerce: {
          ...baseTabs.commerce,
          ...(draftTabs.commerce || {}),
        } as ListTabContent,
      },
      filtersButtonLabel:
        incoming.housingsPage?.filtersButtonLabel ||
        base.housingsPage?.filtersButtonLabel ||
        "",
      totalObjectsLabel:
        incoming.housingsPage?.totalObjectsLabel || base.housingsPage?.totalObjectsLabel || "",
      shownObjectsLabel:
        incoming.housingsPage?.shownObjectsLabel || base.housingsPage?.shownObjectsLabel || "",
      resetFiltersLabel:
        incoming.housingsPage?.resetFiltersLabel || base.housingsPage?.resetFiltersLabel || "",
      allObjectsLabel:
        incoming.housingsPage?.allObjectsLabel || base.housingsPage?.allObjectsLabel || "",
      excludeUnknownDeadlinesLabel:
        incoming.housingsPage?.excludeUnknownDeadlinesLabel ||
        base.housingsPage?.excludeUnknownDeadlinesLabel ||
        "",
      foundObjectsLabel: (shownCount, totalCount) => `Найдено ${shownCount} из ${totalCount}`,
      deadlineRangeLabel: (startYear, endYear) => `Срок сдачи: ${startYear} - ${endYear}`,
    },
    housingDetails: {
      ...base.housingDetails,
      ...(incoming.housingDetails || {}),
      factLabels: {
        ...base.housingDetails!.factLabels,
        ...(incoming.housingDetails?.factLabels || {}),
      },
    } as ClientConfig["housingDetails"],
    pricing: {
      ...base.pricing,
      ...(incoming.pricing || {}),
    } as ClientConfig["pricing"],
    housingAccess: {
      ...(base.housingAccess || {}),
      ...(incoming.housingAccess || {}),
    },
    showcase: {
      enabledHousingTypes:
        incoming.showcase?.enabledHousingTypes ||
        base.showcase?.enabledHousingTypes ||
        ["residential", "commerce"],
      heroLabel: incoming.showcase?.heroLabel || base.showcase?.heroLabel || "",
      heroTitle: incoming.showcase?.heroTitle || base.showcase?.heroTitle || "",
      heroDescription:
        incoming.showcase?.heroDescription || base.showcase?.heroDescription || "",
      contactName:
        incoming.showcase?.contactName ?? base.showcase?.contactName ?? "",
      phone: incoming.showcase?.phone ?? base.showcase?.phone ?? "",
      whatsapp: incoming.showcase?.whatsapp ?? base.showcase?.whatsapp ?? "",
      telegram: incoming.showcase?.telegram ?? base.showcase?.telegram ?? "",
      instagram: incoming.showcase?.instagram ?? base.showcase?.instagram ?? "",
      whatsappNumber:
        incoming.showcase?.whatsappNumber ?? base.showcase?.whatsappNumber ?? "",
      whatsappButtonLabel:
        incoming.showcase?.whatsappButtonLabel ??
        base.showcase?.whatsappButtonLabel ??
        "Отправить расчёт в WhatsApp",
      logoMode: incoming.showcase?.logoMode || base.showcase?.logoMode || "text",
      logoLayout:
        incoming.showcase?.logoLayout || base.showcase?.logoLayout || "text-right",
      logoWidth: incoming.showcase?.logoWidth || base.showcase?.logoWidth || 240,
      logoImageHeight:
        incoming.showcase?.logoImageHeight || base.showcase?.logoImageHeight || 52,
      logoTextSize:
        incoming.showcase?.logoTextSize || base.showcase?.logoTextSize || 34,
      logoTextColor:
        incoming.showcase?.logoTextColor || base.showcase?.logoTextColor || "#101828",
      logoText: incoming.showcase?.logoText || base.showcase?.logoText || "",
      logoSrc: incoming.showcase?.logoSrc || base.showcase?.logoSrc,
      logoAlt: incoming.showcase?.logoAlt || base.showcase?.logoAlt,
      palette: getPalette(incoming.showcase?.palette),
    },
  };
};

export const defaultClientConfig = resolveClientConfig(defaultClientConfigDraft);

export const getClientConfigByBrand = (brand?: string) => {
  const normalizedBrand = normalizeBrandSlug(brand || "");
  return resolveClientConfig(
    builtinBrandDrafts[normalizedBrand] || defaultClientConfigDraft,
  );
};

export const cloneClientConfigDraft = (draft?: ClientConfigDraft): ClientConfigDraft =>
  JSON.parse(JSON.stringify(draft || defaultClientConfigDraft));

const matchesRule = (value: string | undefined, exactList?: string[], includesList?: string[]) => {
  if (!exactList?.length && !includesList?.length) {
    return false;
  }

  const normalizedValue = normalizeText(value || "");
  const exactMatch = exactList?.some((item) => normalizeText(item) === normalizedValue);
  const includesMatch = includesList?.some((item) =>
    normalizedValue.includes(normalizeText(item)),
  );

  return Boolean(exactMatch || includesMatch);
};

export const filterHousingsByClient = (
  housings: any[] | null | undefined,
  type: HousingType,
  config: ClientConfig = defaultClientConfig,
) => {
  if (housings == null) {
    return housings;
  }

  if (!Array.isArray(housings)) {
    return [];
  }

  const rule = config.housingAccess[type];

  if (!rule) {
    return housings;
  }

  return housings.filter((housing) => {
    if (rule.ids?.includes(Number(housing?.id))) {
      return true;
    }

    if (
      matchesRule(
        housing?.housingName,
        rule.housingNames,
        rule.housingNameIncludes,
      )
    ) {
      return true;
    }

    if (matchesRule(housing?.builder, rule.builders, rule.builderIncludes)) {
      return true;
    }

    return false;
  });
};
